// framework/client/helpers/MessagingClient/NewChatModal.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { RpcDataProvider } from "../../../core/RpcDataProvider.js"; 
import { createAvatarElement } from "../avatarHelper.js"; 

export class NewChatModal extends ComponentBase {
    static styles = `
        .new-chat-modal .modal-dialog {
            max-width: 500px; /* Or your preferred size */
        }
        .new-chat-modal .modal-body {
            min-height: 300px; /* Ensure enough space for lists */
            max-height: 70vh;
            overflow-y: auto;
        }
        .contact-search-input {
            margin-bottom: 1rem;
        }
        .contact-list-item, .selected-member-item {
            display: flex;
            align-items: center;
            padding: 0.5rem 0.75rem;
            cursor: pointer;
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .contact-list-item:hover, .selected-member-item:hover {
            background-color: var(--bs-tertiary-bg);
        }
        .contact-list-item.selected { /* If you implement direct selection highlighting */
            background-color: var(--bs-primary-bg-subtle);
        }
        .contact-avatar-small {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: var(--bs-secondary);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            margin-right: 0.75rem;
            flex-shrink: 0;
        }
        .contact-avatar-small img {
             width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
        }
        .contact-name {
            flex-grow: 1;
        }
        .add-to-group-btn, .remove-from-group-btn {
            font-size: 0.9rem;
            padding: 0.2rem 0.5rem;
        }
        .selected-members-container {
            margin-top: 1rem;
            border-top: 1px solid var(--bs-border-color);
            padding-top: 1rem;
        }
        .selected-members-list {
            max-height: 150px;
            overflow-y: auto;
        }
    `;

    constructor(config) { // Expects { appClient, instanceId }
        super(config);
        this.modalElement = null;
        this.modalInstance = null;
        this.currentMode = 'dm'; // 'dm' or 'group'
        this.searchTerm = '';
        this.searchResults = []; // For contact search
        this.groupName = '';
        this.groupMembers = new Map(); // contactId -> contactData for selected group members
        this.myContactId = this.appClient.getSharedData('myMessagingContactId');

        // Debounce for search
        this.searchDebounceTimeout = null;

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.contactDataProvider = new RpcDataProvider({ // For searching contacts
            appClient: this.appClient,
            rpcClass: "MessagingRpc",
            eventGroup: `${this.config.instanceId}-contactProvider`,
            autoLoad: false // Load on demand
        });
        await this.contactDataProvider.initialize();

        this.createModalElement();
    }

    createModalElement() {
        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade new-chat-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.setAttribute('aria-labelledby', `${this.config.instanceId}-modalLabel`);
        this.modalElement.setAttribute('aria-hidden', 'true');

        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="${this.config.instanceId}-modalLabel" data-i18n="New Chat">New Chat</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Tabs for DM / Group -->
                        <ul class="nav nav-tabs mb-3" id="${this.config.instanceId}-modeTabs" role="tablist">
                            <li class="nav-item" role="presentation">
                                <button class="nav-link active" id="${this.config.instanceId}-dm-tab" data-bs-toggle="tab" data-bs-target="#${this.config.instanceId}-dm-pane" type="button" role="tab" aria-controls="dm-pane" aria-selected="true" data-mode="dm" data-i18n="Direct Message">Direct Message</button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" id="${this.config.instanceId}-group-tab" data-bs-toggle="tab" data-bs-target="#${this.config.instanceId}-group-pane" type="button" role="tab" aria-controls="group-pane" aria-selected="false" data-mode="group" data-i18n="New Group">New Group</button>
                            </li>
                        </ul>
                        <div class="tab-content" id="${this.config.instanceId}-tabContent">
                            <div class="tab-pane fade show active" id="${this.config.instanceId}-dm-pane" role="tabpanel" aria-labelledby="dm-tab">
                                <input type="search" class="form-control contact-search-input" id="${this.config.instanceId}-dm-search" placeholder="${this.t('Search contacts...')}" data-i18n-placeholder="Search contacts...">
                                <div id="${this.config.instanceId}-dm-results" class="contact-results-list list-group"></div>
                            </div>
                            <div class="tab-pane fade" id="${this.config.instanceId}-group-pane" role="tabpanel" aria-labelledby="group-tab">
                                <div class="mb-3">
                                    <label for="${this.config.instanceId}-group-name" class="form-label" data-i18n="Group Name">Group Name</label>
                                    <input type="text" class="form-control" id="${this.config.instanceId}-group-name" placeholder="${this.t('Enter group name')}" data-i18n-placeholder="Enter group name">
                                </div>
                                <input type="search" class="form-control contact-search-input" id="${this.config.instanceId}-group-search" placeholder="${this.t('Search contacts to add...')}" data-i18n-placeholder="Search contacts to add...">
                                <div id="${this.config.instanceId}-group-results" class="contact-results-list list-group mb-3"></div>
                                <div class="selected-members-container">
                                    <h6 data-i18n="Selected Members">Selected Members:</h6>
                                    <div id="${this.config.instanceId}-selected-members" class="selected-members-list list-group list-group-flush"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" data-i18n="Cancel">Cancel</button>
                        <button type="button" class="btn btn-primary" id="${this.config.instanceId}-action-btn" data-i18n="Start Chat">Start Chat</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);

        // Attach event listeners
        this.modalElement.querySelectorAll(`#${this.config.instanceId}-modeTabs .nav-link`).forEach(tabEl => {
            tabEl.addEventListener('shown.bs.tab', (event) => {
                this.currentMode = event.target.dataset.mode;
                this.updateActionButtonLabel();
                this.clearSearchResults(this.currentMode); // Clear previous mode's results
            });
        });

        this.modalElement.querySelector(`#${this.config.instanceId}-dm-search`)
            .addEventListener('input', (e) => this.debouncedSearchContacts(e.target.value, 'dm'));
        this.modalElement.querySelector(`#${this.config.instanceId}-group-search`)
            .addEventListener('input', (e) => this.debouncedSearchContacts(e.target.value, 'group'));
        this.modalElement.querySelector(`#${this.config.instanceId}-action-btn`)
            .addEventListener('click', this.handleAction.bind(this));
            
        this.modalElement.addEventListener('hidden.bs.modal', this.resetModal.bind(this));
        this.appClient.tx.applyTranslations(this.modalElement);
    }

    show() {
        this.resetModal(); // Reset state every time it's shown
        this.modalInstance.show();
        // Default to DM tab
        const dmTab = this.modalElement.querySelector(`#${this.config.instanceId}-dm-tab`);
        if (dmTab) {
            const tab = new bootstrap.Tab(dmTab);
            tab.show();
        }
        this.currentMode = 'dm';
        this.updateActionButtonLabel();
    }

    hide() {
        this.modalInstance.hide();
    }
    
    resetModal() {
        this.currentMode = 'dm';
        this.searchTerm = '';
        this.searchResults = [];
        this.groupName = '';
        this.groupMembers.clear();
        this.modalElement.querySelector(`#${this.config.instanceId}-dm-search`).value = '';
        this.modalElement.querySelector(`#${this.config.instanceId}-group-search`).value = '';
        this.modalElement.querySelector(`#${this.config.instanceId}-group-name`).value = '';
        this.clearSearchResults('dm');
        this.clearSearchResults('group');
        this.renderSelectedMembers();
    }

    updateActionButtonLabel() {
        const actionButton = this.modalElement.querySelector(`#${this.config.instanceId}-action-btn`);
        if (this.currentMode === 'dm') {
            actionButton.textContent = this.t('Start Chat');
            actionButton.setAttribute('data-i18n', 'Start Chat');
        } else {
            actionButton.textContent = this.t('Create Group');
            actionButton.setAttribute('data-i18n', 'Create Group');
        }
        this.appClient.tx.applyTranslations(actionButton);
    }

    debouncedSearchContacts(term, mode) {
        clearTimeout(this.searchDebounceTimeout);
        this.searchDebounceTimeout = setTimeout(() => {
            this.searchContacts(term, mode);
        }, 300); // 300ms debounce
    }

    async searchContacts(term, mode) {
        this.searchTerm = term.trim();
        if (this.searchTerm.length < 2 && mode === 'dm') { // Require at least 2 chars for DM search to avoid too many results
             this.clearSearchResults(mode);
             return;
        }
        if (this.searchTerm.length === 0 && mode === 'group') { // Allow empty search for group to show all initially
             // this.clearSearchResults(mode); return;
        }

        try {
            const result = await this.appClient.rpcClient.call('MessagingRpc.listContacts', {
                searchTerm: this.searchTerm,
                pageSize: 10 // Limit results in modal
            }, { projectId: this.appClient.getSharedData('selectedProjectId') });
            this.searchResults = result.data || [];
            this.renderSearchResults(mode);
        } catch (error) {
            this.log.error("Error searching contacts:", error);
            this.appClient.notifier.error("Failed to search contacts.");
        }
    }

    clearSearchResults(mode) {
        const resultsContainerId = mode === 'dm' ? `${this.config.instanceId}-dm-results` : `${this.config.instanceId}-group-results`;
        const resultsContainer = this.modalElement.querySelector(`#${resultsContainerId}`);
        if(resultsContainer) resultsContainer.innerHTML = '';
    }

    renderSearchResults(mode) {
        const resultsContainerId = mode === 'dm' ? `${this.config.instanceId}-dm-results` : `${this.config.instanceId}-group-results`;
        const resultsContainer = this.modalElement.querySelector(`#${resultsContainerId}`);
        resultsContainer.innerHTML = ''; 

        if (this.searchResults.length === 0 && this.searchTerm) {
            resultsContainer.innerHTML = `<p class="text-muted p-2">${this.t('No contacts found.')}</p>`;
            this.appClient.tx.applyTranslations(resultsContainer);
            return;
        }

        this.searchResults.forEach(contact => {
            if (contact.contactId === this.myContactId && mode === 'dm') return;
            if (mode === 'group' && this.groupMembers.has(contact.contactId)) return;

            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'contact-list-item list-group-item list-group-item-action';
            
            // --- USE AVATAR HELPER ---
            const avatarElement = createAvatarElement({
                displayName: contact.displayName,
                avatarUrl: contact.avatarUrl, // This is the relative path
                sizeClass: 'small', // Use a defined size class
                // additionalClasses: ['me-2'] // If app-avatar-small doesn't include margin
            });
            // --- END USE ---

            const nameSpan = document.createElement('span');
            nameSpan.className = 'contact-name';
            nameSpan.textContent = `${contact.displayName} (${this.t(contact.contactType.replace('_', ' '))})`;
            
            item.appendChild(avatarElement); // Append the avatar node
            item.appendChild(nameSpan);

            if (mode === 'group') {
                const addButton = document.createElement('button');
                addButton.className = 'btn btn-sm btn-success add-to-group-btn ms-auto';
                addButton.dataset.contactId = contact.contactId;
                addButton.setAttribute('data-i18n', 'Add');
                addButton.textContent = this.t('Add');
                addButton.addEventListener('click', (e) => {
                    e.stopPropagation(); 
                    this.addGroupMember(contact);
                });
                item.appendChild(addButton);
            } else if (mode === 'dm') {
                item.addEventListener('click', () => {
                    this.emit('dmContactSelected', { contactId: contact.contactId, displayName: contact.displayName });
                    this.hide();
                });
            }
            resultsContainer.appendChild(item);
        });
        this.appClient.tx.applyTranslations(resultsContainer);
    }

    addGroupMember(contactData) {
        if (!this.groupMembers.has(contactData.contactId) && contactData.contactId !== this.myContactId) {
            this.groupMembers.set(contactData.contactId, contactData);
            this.renderSelectedMembers();
            this.renderSearchResults('group'); // Re-render search results to remove added member
        }
    }

    removeGroupMember(contactId) {
        this.groupMembers.delete(contactId);
        this.renderSelectedMembers();
        this.renderSearchResults('group'); // Re-render search results to add member back if still matches filter
    }

    renderSelectedMembers() {
        const selectedContainer = this.modalElement.querySelector(`#${this.config.instanceId}-selected-members`);
        selectedContainer.innerHTML = '';
        if (this.groupMembers.size === 0) {
            selectedContainer.innerHTML = `<p class="text-muted small p-2">${this.t('No members selected yet.')}</p>`;
            this.appClient.tx.applyTranslations(selectedContainer);
            return;
        }

        this.groupMembers.forEach(contact => {
            const item = document.createElement('div');
            item.className = 'selected-member-item list-group-item';
            
            // --- USE AVATAR HELPER ---
            const avatarElement = createAvatarElement({
                displayName: contact.displayName,
                avatarUrl: contact.avatarUrl, // Relative path
                sizeClass: 'small'
            });
            // --- END USE ---
            
            const nameSpan = document.createElement('span');
            nameSpan.className = 'contact-name';
            nameSpan.textContent = contact.displayName;

            const removeButton = document.createElement('button');
            removeButton.className = 'btn btn-sm btn-danger remove-from-group-btn ms-auto';
            removeButton.dataset.contactId = contact.contactId;
            removeButton.setAttribute('data-i18n', 'Remove');
            removeButton.textContent = this.t('Remove');
            removeButton.addEventListener('click', () => this.removeGroupMember(contact.contactId));

            item.appendChild(avatarElement); // Append avatar node
            item.appendChild(nameSpan);
            item.appendChild(removeButton);
            selectedContainer.appendChild(item);
        });

        this.appClient.tx.applyTranslations(selectedContainer);
    }

    async handleAction() {
        if (this.currentMode === 'dm') {
            // DM mode action is handled by clicking a contact in the list, which emits 'dmContactSelected'
            // This button might not be strictly needed for DM if selection implies action.
            // Or, it could confirm the selected contact from a list if multiple are shown.
            // For now, we'll assume direct click on search result is enough for DM.
            const selectedDmContact = this.modalElement.querySelector(`#${this.config.instanceId}-dm-results .contact-list-item.selected`); // If using selection highlight
            if (selectedDmContact) {
                 const contactId = selectedDmContact.dataset.contactId; // Assuming you store contactId in data attribute
                 const displayName = selectedDmContact.querySelector('.contact-name').textContent;
                 this.emit('dmContactSelected', { contactId, displayName });
                 this.hide();
            } else {
                this.appClient.notifier.info("Please select a contact to start a chat.");
            }

        } else if (this.currentMode === 'group') {
            this.groupName = this.modalElement.querySelector(`#${this.config.instanceId}-group-name`).value.trim();
            if (!this.groupName) {
                this.appClient.notifier.error("Group name is required.");
                return;
            }
            if (this.groupMembers.size === 0) {
                this.appClient.notifier.error("Please add at least one member to the group.");
                return;
            }
            const memberContactIds = Array.from(this.groupMembers.keys());
            this.emit('groupCreationRequested', { name: this.groupName, memberContactIds });
            this.hide();
        }
    }

    render() { // Main render for the component itself (usually not needed if modal is handled via show/hide)
        // This component primarily manages a modal, so its own direct render might be minimal or not used
        // if it's always controlled via .show() and .hide() from MessagingClient.
        // If it needs a persistent button in the UI, that would go here.
        return this.container; // container might be null if not used for a persistent button
    }

    destroy() {
        if (this.modalElement) {
            this.modalInstance?.dispose();
            this.modalElement.remove();
            this.modalElement = null;
        }
        super.destroy();
    }
}